import { useState } from "react";
import { useTodoContext, type Todo } from "../../../contexts/TodoContext";
import { useRole } from "../../../contexts/RoleContext";

const TAG_COLORS: Record<string, string> = {
  "결재": "text-[#7A9BFF] bg-[#4B6CF7]/10 border-[#4B6CF7]/20",
  "계약": "text-amber-500/80 bg-amber-500/10 border-amber-500/20",
  "위기대응": "text-red-400/80 bg-red-400/10 border-red-400/20",
  "이벤트 대응": "text-red-400/80 bg-red-400/10 border-red-400/20",
  "SNS": "text-pink-400/80 bg-pink-400/10 border-pink-400/20",
  "세금": "text-orange-400/80 bg-orange-400/10 border-orange-400/20",
  "예산": "text-emerald-500/80 bg-emerald-500/10 border-emerald-500/20",
  "법무": "text-teal-400/80 bg-teal-400/10 border-teal-400/20",
  "투자": "text-cyan-400/80 bg-cyan-400/10 border-cyan-400/20",
};

function getTagColor(tag?: string) {
  if (!tag) return "text-[#555555] border-black/[0.08]";
  if (tag === "위기대응" || tag === "법적대응" || tag === "PR") return TAG_COLORS["위기대응"];
  return TAG_COLORS[tag] ?? "text-[#AAAAAA] bg-black/[0.04] border-black/[0.07]";
}

function PriorityBadge({ priority }: { priority: Todo["priority"] }) {
  if (priority === "high") return <span className="text-[9px] px-1.5 py-0.5 rounded border text-red-400 border-red-200 bg-red-50 whitespace-nowrap">긴급</span>;
  if (priority === "medium") return <span className="text-[9px] px-1.5 py-0.5 rounded border text-amber-500 border-amber-200 bg-amber-50 whitespace-nowrap">보통</span>;
  return <span className="text-[9px] px-1.5 py-0.5 rounded border text-[#BBBBBB] border-black/[0.08] whitespace-nowrap">낮음</span>;
}

interface TodoDetailPanelProps {
  todo: Todo;
  onClose: () => void;
  onToggle: () => void;
}

function TodoDetailPanel({ todo, onClose, onToggle }: TodoDetailPanelProps) {
  return (
    <div className="absolute inset-0 bg-white rounded-xl z-10 flex flex-col p-4 shadow-lg border border-black/[0.08]">
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex items-center gap-1.5">
          {todo.tag && (
            <span className={`text-[9px] px-1.5 py-0.5 rounded border shrink-0 ${getTagColor(todo.tag)}`}>
              {todo.tag}
            </span>
          )}
          <PriorityBadge priority={todo.priority} />
          {todo.source === "event" && (
            <span className="text-[9px] px-1.5 py-0.5 rounded border text-red-400/80 bg-red-400/10 border-red-400/20 animate-pulse">
              이벤트
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="w-5 h-5 flex items-center justify-center text-[#BBBBBB] hover:text-[#555555] transition-colors shrink-0 cursor-pointer"
        >
          <i className="ri-close-line text-sm"></i>
        </button>
      </div>

      {/* Title */}
      <p className={`text-[12px] font-semibold leading-snug mb-3 ${todo.done ? "line-through text-[#BBBBBB]" : "text-[#222222]"}`}>
        {todo.text}
      </p>

      {/* Description */}
      {todo.desc ? (
        <div className="bg-[#F8F8F8] rounded-lg p-3 flex-1 overflow-y-auto mb-3">
          <p className="text-[11px] text-[#555555] leading-relaxed">{todo.desc}</p>
        </div>
      ) : (
        <div className="bg-[#F8F8F8] rounded-lg p-3 flex-1 flex items-center justify-center mb-3">
          <p className="text-[11px] text-[#BBBBBB] text-center">이벤트에서 자동 생성된 할 일입니다.<br/>위의 내용을 확인하고 업무를 진행하세요.</p>
        </div>
      )}

      {/* Actions */}
      <button
        type="button"
        onClick={() => { onToggle(); onClose(); }}
        className={`w-full py-2 rounded-lg text-[11px] font-semibold cursor-pointer transition-all whitespace-nowrap ${
          todo.done
            ? "bg-black/[0.06] text-[#888888] hover:bg-black/[0.10]"
            : "bg-[#4B6CF7]/10 text-[#4B6CF7] hover:bg-[#4B6CF7]/20"
        }`}
      >
        {todo.done ? (
          <><i className="ri-refresh-line mr-1.5"></i>완료 취소하기</>
        ) : (
          <><i className="ri-checkbox-circle-line mr-1.5"></i>완료로 표시하기</>
        )}
      </button>
    </div>
  );
}

export default function TodoList() {
  const { todos, toggle, clearDone } = useTodoContext();
  const { roleConfig } = useRole();
  const [selectedTodo, setSelectedTodo] = useState<Todo | null>(null);

  const doneCount = todos.filter((t) => t.done).length;
  const eventTodos = todos.filter((t) => t.source === "event" && !t.done);

  const handleRowClick = (todo: Todo) => {
    setSelectedTodo(todo);
  };

  const handleCheckboxClick = (e: React.MouseEvent, todoId: string) => {
    e.stopPropagation();
    toggle(todoId);
  };

  return (
    <div className="bg-white border border-black/[0.08] rounded-xl p-4 h-full flex flex-col relative overflow-hidden">
      {/* Detail Panel Overlay */}
      {selectedTodo && (
        <TodoDetailPanel
          todo={selectedTodo}
          onClose={() => setSelectedTodo(null)}
          onToggle={() => toggle(selectedTodo.id)}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <h3 className="text-[#888888] text-xs font-medium uppercase tracking-widest">오늘의 할 일</h3>
          {eventTodos.length > 0 && (
            <span className="text-[9px] bg-red-400/10 text-red-400/80 border border-red-400/20 px-1.5 py-0.5 rounded-full font-semibold animate-pulse">
              이벤트 {eventTodos.length}건
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {doneCount > 0 && (
            <button
              type="button"
              onClick={clearDone}
              className="text-[#CCCCCC] hover:text-[#888888] text-[10px] cursor-pointer whitespace-nowrap transition-colors"
            >
              완료 삭제
            </button>
          )}
          <span className="text-[#BBBBBB] text-[10px] tabular-nums">{doneCount}/{todos.length}</span>
        </div>
      </div>

      {/* Role badge */}
      <div className="flex items-center gap-1.5 mb-2.5">
        <i className={`${roleConfig.icon} text-[#CCCCCC] text-xs`}></i>
        <span className="text-[#CCCCCC] text-[10px]">{roleConfig.label} 업무 목록</span>
        <span className="text-[#DDDDDD] text-[9px] ml-auto">클릭 시 상세보기</span>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-black/[0.06] rounded-full h-px mb-3">
        <div
          className="h-px rounded-full bg-[#4B6CF7]/60 transition-all duration-500"
          style={{ width: `${todos.length > 0 ? (doneCount / todos.length) * 100 : 0}%` }}
        />
      </div>

      {/* Todo list */}
      <div className="space-y-0.5 flex-1 overflow-y-auto">
        {todos.map((todo) => (
          <div
            key={todo.id}
            onClick={() => handleRowClick(todo)}
            className={`flex items-start gap-2.5 px-2 py-2 rounded-lg cursor-pointer transition-all hover:bg-black/[0.03] group ${
              todo.done ? "opacity-40" : ""
            } ${todo.source === "event" && !todo.done ? "bg-red-400/[0.03] border border-red-400/[0.08] hover:bg-red-400/[0.06]" : ""}`}
          >
            {/* Checkbox — click stops propagation so it doesn't open detail */}
            <div
              onClick={(e) => handleCheckboxClick(e, todo.id)}
              className={`w-3.5 h-3.5 flex items-center justify-center rounded border mt-0.5 shrink-0 transition-colors cursor-pointer ${
                todo.done
                  ? "bg-[#4B6CF7]/20 border-[#4B6CF7]/30"
                  : todo.source === "event"
                  ? "border-red-400/30 group-hover:border-red-400/50"
                  : "border-black/[0.15] group-hover:border-black/30"
              }`}
            >
              {todo.done && <i className="ri-check-line text-[#4B6CF7] text-[8px]"></i>}
            </div>

            {/* Text */}
            <p
              className={`text-[11px] leading-relaxed flex-1 ${
                todo.done
                  ? "line-through text-[#CCCCCC]"
                  : todo.source === "event"
                  ? "text-[#444444] font-medium"
                  : "text-[#555555]"
              }`}
            >
              {todo.text}
            </p>

            {/* Tag or Priority */}
            {todo.tag ? (
              <span className={`text-[9px] px-1.5 py-0.5 rounded border shrink-0 whitespace-nowrap ${getTagColor(todo.tag)}`}>
                {todo.tag}
              </span>
            ) : (
              <PriorityBadge priority={todo.priority} />
            )}

            {/* Arrow indicator */}
            <i className="ri-arrow-right-s-line text-[#DDDDDD] text-xs shrink-0 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity"></i>
          </div>
        ))}

        {todos.length === 0 && (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <i className="ri-checkbox-circle-line text-2xl text-[#DDDDDD] mb-2"></i>
            <p className="text-[#CCCCCC] text-xs">모든 할 일을 완료했어요!</p>
          </div>
        )}
      </div>
    </div>
  );
}
